import React, { useState } from 'react';
import { colors, fonts } from '../brand';

/**
 * Accordion — renders a list of collapsible panels.
 *
 * Props:
 *   items: [{ label: string, children: ReactNode }]
 *   defaultOpen: number | 'all' | null  — index to open by default, or 'all'
 *   allowMultiple: bool — if true, multiple panels can be open at once
 */
export default function Accordion({ items, defaultOpen = null, allowMultiple = false }) {
  const initOpen = () => {
    if (defaultOpen === 'all') return items.map((_, i) => i);
    if (defaultOpen !== null) return [defaultOpen];
    return [];
  };

  const [openIndexes, setOpenIndexes] = useState(initOpen);

  const toggle = (i) => {
    if (allowMultiple) {
      setOpenIndexes(prev =>
        prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
      );
    } else {
      setOpenIndexes(prev => prev.includes(i) ? [] : [i]);
    }
  };

  return (
    <div style={styles.wrapper}>
      {items.map((item, i) => {
        const isOpen = openIndexes.includes(i);
        return (
          <div key={i} style={styles.panel}>
            <button
              style={{ ...styles.trigger, ...(isOpen ? styles.triggerOpen : {}) }}
              onClick={() => toggle(i)}
              aria-expanded={isOpen}
            >
              <span style={styles.label}>{item.label}</span>
              <span style={{ ...styles.icon, ...(isOpen ? styles.iconOpen : {}) }}>›</span>
            </button>
            {isOpen && (
              <div style={styles.content}>
                {item.children}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: '0' },
  panel: { borderBottom: `1px solid ${colors.peach}` },
  trigger: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    gap: '16px',
  },
  triggerOpen: {},
  label: { fontFamily: fonts.heading, color: colors.navy, fontSize: '15px', fontWeight: '600', letterSpacing: '0.3px' },
  icon: { color: colors.rose, fontSize: '20px', fontWeight: '300', flexShrink: 0, transform: 'rotate(0deg)', transition: 'transform 0.2s ease', display: 'inline-block' },
  iconOpen: { transform: 'rotate(90deg)' },
  content: { paddingBottom: '20px' },
};
